/**
 * EU Cyber Resilience Act — conformity-pack domain model.
 *
 * WHY THIS IS IN THE SHARED PACKAGE
 * The same enumerations, the same category→conformity-route rule and the same
 * Article 14 reporting clocks are needed on both sides of the wire: the API
 * validates and persists with them, the web client renders chips, drives the
 * create/edit forms and ticks the countdown without polling. One definition
 * here is what stops the two disagreeing.
 *
 * SCOPE
 * This is the CONFORMITY ARTEFACT layer — the per-product record a manufacturer
 * must be able to produce (classification, conformity route, SBOM reference,
 * support period, EU Declaration of Conformity, vulnerability handling and the
 * Article 14 reports). It is distinct from, and sits on top of, the `cra`
 * self-assessment FRAMEWORK that already exists: the assessment answers "does
 * our organisation meet the essential requirements?", this layer answers "for
 * THIS product, what do we need on file, and what is missing?".
 *
 * SOURCE: Regulation (EU) 2024/2847 of the European Parliament and of the
 * Council of 23 October 2024 (OJ L, 2024/2847, 20.11.2024). Public EU
 * legislation, paraphrased. Article and Annex citations are given where we are
 * confident of them; this content has not been reviewed by qualified counsel.
 */
import { formatDuration } from './nis2Incident.js';
// ---------------------------------------------------------------------------
//  Enumerations
// ---------------------------------------------------------------------------
/**
 * The CRA product classes.
 *
 * DEFAULT is the residual class ("not important, not critical"). The two
 * IMPORTANT classes and CRITICAL come from Annex III and Annex IV respectively;
 * the Regulation lists the categories, and the class decides which conformity
 * routes are open to the manufacturer (see `CONFORMITY_ROUTE_RULES`).
 */
export const CRA_PRODUCT_CATEGORIES = [
    'DEFAULT',
    'IMPORTANT_CLASS_I',
    'IMPORTANT_CLASS_II',
    'CRITICAL',
];
/**
 * The conformity-assessment route (Article 32 / Annex VIII).
 *
 * SELF_ASSESSMENT is the internal-control procedure (Annex VIII, Module A),
 * available to default-class products and to important-class products that
 * fully apply the relevant harmonised standards. THIRD_PARTY covers the
 * procedures that involve a notified body (EU type-examination / Module B+C, or
 * full quality assurance / Module H) and, for critical products, a European
 * cybersecurity certificate.
 */
export const CRA_CONFORMITY_ROUTES = ['SELF_ASSESSMENT', 'THIRD_PARTY'];
/**
 * Where a product sits in its journey to a CE mark.
 *
 * These are workflow states the manufacturer sets, NOT an assertion by the
 * product that conformity has been achieved — CE_MARKED means the customer has
 * recorded that they affixed the mark, exactly as the incident module records
 * what the customer says they filed. OpenComply marks nothing itself.
 */
export const CRA_PRODUCT_STATUSES = ['DRAFT', 'IN_PROGRESS', 'READY', 'CE_MARKED'];
/** Severity of a handled vulnerability, on the same ramp as incidents. */
export const CRA_VULN_SEVERITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
/** Lifecycle of a vulnerability record (Annex I Part II, coordinated disclosure). */
export const CRA_VULN_STATUSES = ['OPEN', 'FIXED', 'DISCLOSED'];
// ---------------------------------------------------------------------------
//  Category → conformity route
// ---------------------------------------------------------------------------
/**
 * For each class, the route the product DEFAULTS to and whether a plain
 * self-assessment is permitted at all.
 *
 *   DEFAULT / IMPORTANT_CLASS_I  — self-assessment allowed (Class I may self-
 *                                  assess when harmonised standards are fully
 *                                  applied; otherwise a third-party route).
 *   IMPORTANT_CLASS_II           — defaults to third-party; the Regulation
 *                                  steers Class II to a notified-body route.
 *   CRITICAL                     — third-party only. A European cybersecurity
 *                                  certificate / notified body is required, so
 *                                  self-assessment is NOT a valid choice.
 */
export const CONFORMITY_ROUTE_RULES = {
    DEFAULT: { default: 'SELF_ASSESSMENT', selfAssessmentAllowed: true },
    IMPORTANT_CLASS_I: { default: 'SELF_ASSESSMENT', selfAssessmentAllowed: true },
    IMPORTANT_CLASS_II: { default: 'THIRD_PARTY', selfAssessmentAllowed: true },
    CRITICAL: { default: 'THIRD_PARTY', selfAssessmentAllowed: false },
};
/** The route a freshly-classified product should start on. */
export function defaultConformityRoute(category) {
    return CONFORMITY_ROUTE_RULES[category].default;
}
/**
 * Is this (category, route) pair permitted?
 *
 * The only hard rule the Regulation gives us a bright line for is that a
 * critical product may not take the self-assessment route. Everything else is
 * allowed (a manufacturer may always choose a STRICTER route than the minimum),
 * so this returns false in exactly one case rather than trying to encode the
 * full Annex VIII decision tree, which depends on facts we do not hold (whether
 * harmonised standards are fully applied).
 */
export function isConformityRouteAllowed(category, route) {
    if (route === 'SELF_ASSESSMENT' && !CONFORMITY_ROUTE_RULES[category].selfAssessmentAllowed) {
        return false;
    }
    return true;
}
/**
 * Resolve the route to store from what the user asked for.
 *
 * Undefined input takes the category default. A disallowed explicit choice is
 * coerced UP to third-party rather than rejected: a critical product that was
 * sent SELF_ASSESSMENT is a mistake with one safe correction, and silently
 * upgrading is safer than persisting an invalid state. The caller can still
 * surface a warning; the data is never left wrong.
 */
export function resolveConformityRoute(category, requested) {
    if (!requested)
        return defaultConformityRoute(category);
    if (!isConformityRouteAllowed(category, requested))
        return 'THIRD_PARTY';
    return requested;
}
// ---------------------------------------------------------------------------
//  Article 14 reporting clocks
// ---------------------------------------------------------------------------
/**
 * Article 14 — reporting of actively-exploited vulnerabilities and severe
 * incidents to ENISA (routed to the CSIRTs).
 *
 * The staged sequence, paraphrased from Article 14(2):
 *   (a) EARLY WARNING    — without undue delay and in any event within 24 hours
 *                          of becoming aware.
 *   (b) NOTIFICATION     — without undue delay and in any event within 72 hours
 *                          of becoming aware.
 *   (c) FINAL REPORT     — once a corrective or mitigating measure is available;
 *                          the Regulation sets a short window after that point.
 *                          We do NOT compute a deadline for it, because the
 *                          clock starts from "a fix is available", a fact this
 *                          record does not pin to a timestamp. We record WHEN
 *                          the final report was made, and leave the judgement
 *                          of its due date to the manufacturer — the same
 *                          restraint the NIS2 module shows for its on-request
 *                          intermediate report.
 *
 * The clock only exists for an ACTIVELY EXPLOITED vulnerability; an ordinary
 * handled vulnerability has no Article 14 obligation, so no deadline is shown.
 * "Without undue delay" is the real obligation — 24 and 72 hours are the outer
 * limits, not a budget to spend.
 *
 * NOTE ON JURISDICTION: Article 14 reporting applies earlier than most of the
 * Regulation. OpenComply never transmits anything; it records what the customer
 * says they reported, by when.
 */
export const CRA_EARLY_WARNING_HOURS = 24;
export const CRA_NOTIFICATION_HOURS = 72;
/** The Article 14 stages that carry a deadline we can derive. */
export const CRA_SCHEDULED_STAGES = ['EARLY_WARNING', 'NOTIFICATION'];
const DUE_SOON_FRACTION = 0.25;
function toDate(value) {
    return value instanceof Date ? value : new Date(value);
}
function describe(stage, dueAt, windowMs, reportedAt, now) {
    const msRemaining = dueAt.getTime() - now.getTime();
    if (reportedAt) {
        const lateBy = reportedAt.getTime() - dueAt.getTime();
        return {
            stage,
            dueAt: dueAt.toISOString(),
            state: lateBy > 0 ? 'SUBMITTED_LATE' : 'SUBMITTED_ON_TIME',
            msRemaining,
            msLateBy: lateBy > 0 ? lateBy : null,
            reportedAt: reportedAt.toISOString(),
        };
    }
    let state = 'OPEN';
    if (msRemaining <= 0)
        state = 'OVERDUE';
    else if (msRemaining <= windowMs * DUE_SOON_FRACTION)
        state = 'DUE_SOON';
    return { stage, dueAt: dueAt.toISOString(), state, msRemaining, msLateBy: null, reportedAt: null };
}
/**
 * The Article 14 reporting clocks for one vulnerability.
 *
 * Returns an empty list for a vulnerability that is not actively exploited: no
 * obligation, no countdown, no false urgency.
 */
export function craVulnerabilityDeadlines(input) {
    if (!input.activelyExploited)
        return [];
    const now = input.now ?? new Date();
    const awareAt = toDate(input.awareAt);
    const earlyWindow = CRA_EARLY_WARNING_HOURS * 3_600_000;
    const notifyWindow = CRA_NOTIFICATION_HOURS * 3_600_000;
    return [
        describe('EARLY_WARNING', new Date(awareAt.getTime() + earlyWindow), earlyWindow, input.earlyWarningAt ? toDate(input.earlyWarningAt) : null, now),
        describe('NOTIFICATION', new Date(awareAt.getTime() + notifyWindow), notifyWindow, input.notificationAt ? toDate(input.notificationAt) : null, now),
    ];
}
/** True when any derivable Article 14 deadline has been missed. */
export function craHasMissedDeadline(deadlines) {
    return deadlines.some((d) => d.state === 'OVERDUE' || d.state === 'SUBMITTED_LATE');
}
/** Re-exported so the web countdown and the API share one duration formatter. */
export { formatDuration };
// ---------------------------------------------------------------------------
//  CE readiness
// ---------------------------------------------------------------------------
/**
 * The CE-readiness checklist items, one per obligation a manufacturer must have
 * satisfied before affixing the CE mark.
 *
 * These are STABLE KEYS, not display text — the API computes a status per key
 * and the web client renders a localised label and the article citation. The
 * keys map to the Annexes they come from:
 *
 *   essentialRequirements   — Annex I Part I (essential cybersecurity reqs),
 *                             evidenced by the `cra` self-assessment.
 *   vulnerabilityHandling   — Annex I Part II (coordinated disclosure, SBOM,
 *                             security updates), evidenced by the assessment
 *                             and by a vulnerability-handling record existing.
 *   technicalDocumentation  — Annex VII (technical documentation).
 *   sbom                    — Annex I Part II(1): the software bill of materials.
 *   conformityAssessment    — Annex VIII / Article 32: the right route carried
 *                             out for the product's class.
 *   declarationOfConformity — Annex V: the EU Declaration of Conformity exists.
 *   supportPeriod           — Article 13(8): a defined support period.
 */
export const CE_CHECKLIST_KEYS = [
    'essentialRequirements',
    'vulnerabilityHandling',
    'technicalDocumentation',
    'sbom',
    'conformityAssessment',
    'declarationOfConformity',
    'supportPeriod',
];
/** The CRA Annex / Article each checklist key is anchored to. */
export const CE_CHECKLIST_REFERENCE = {
    essentialRequirements: 'Annex I Part I',
    vulnerabilityHandling: 'Annex I Part II',
    technicalDocumentation: 'Annex VII',
    sbom: 'Annex I Part II(1)',
    conformityAssessment: 'Annex VIII · Art 32',
    declarationOfConformity: 'Annex V',
    supportPeriod: 'Art 13(8)',
};
/**
 * Roll a set of checklist items up to one verdict and the met/applicable
 * counts. Pure, so both the API and tests call the identical logic.
 */
export function summariseCeReadiness(items) {
    const applicable = items.filter((i) => i.status !== 'NOT_APPLICABLE');
    const met = applicable.filter((i) => i.status === 'MET');
    const anyMissing = applicable.some((i) => i.status === 'MISSING');
    const anyPartial = applicable.some((i) => i.status === 'PARTIAL');
    let status = 'READY';
    if (anyMissing)
        status = 'NOT_READY';
    else if (anyPartial)
        status = 'NEARLY';
    return {
        status,
        items,
        metCount: met.length,
        applicableCount: applicable.length,
    };
}
//# sourceMappingURL=cra.js.map